import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import MatchDecisionPanel from '../../components/MatchDecisionPanel';

const MENTIXX_GREEN = 'from-[#8FFF8B] to-[#53DD6C]';
const SCALE = [1, 2, 3, 4, 5];

export default function FeedbackFormMentee() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const userId = searchParams.get('uid');
  const mentorId = searchParams.get('mentor_id');
  const milestone = searchParams.get('milestone');

  const [goalsList, setGoalsList] = useState([]);
  const [competencesList, setCompetencesList] = useState([]);
  const [goals, setGoals] = useState({});
  const [competences, setCompetences] = useState({});
  const [mentorRating, setMentorRating] = useState(null);
  const [satisfaction, setSatisfaction] = useState(null);
  const [comments, setComments] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showDecisionPanel, setShowDecisionPanel] = useState(false);
  const [error, setError] = useState('');

  // Ziele und Kompetenzen laden
  useEffect(() => {
    async function fetchSharedTopics() {
      try {
        const res = await axios.get(
          `/mentoring/feedback/form?mentor_id=${mentorId}&mentee_id=${userId}`,
          { withCredentials: true }
        );
        setGoalsList(res.data.shared_goals || []);
        setCompetencesList(res.data.shared_competences || []);
      } catch (err) {
        setGoalsList([]);
        setCompetencesList([]);
      }
    }
    if (userId && mentorId) fetchSharedTopics();
  }, [userId, mentorId]);

  // Bewertung von Zielen und Kompetenzen setzen
  const handleRate = (type, key, value) => {
    if (type === 'goal') setGoals((prev) => ({ ...prev, [key]: value }));
    else setCompetences((prev) => ({ ...prev, [key]: value }));
  };

  // Pflichtfelder prüfen
  const isValid =
    Object.keys(goals).length === goalsList.length &&
    Object.keys(competences).length === competencesList.length &&
    satisfaction &&
    mentorRating;

  // Absenden und Popup anzeigen
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isValid) {
      setError('Bitte alle Felder ausfüllen.');
      return;
    }
    setError('');
    try {
      const payload = {
        user_id: Number(userId),
        role: 'mentee',
        milestone,
        source: 'reminder',
        target_user_id: Number(mentorId),
        goals,
        competences,
        comments,
        satisfaction_score: Number(satisfaction),
        mentor_rating: Number(mentorRating),
      };
      await axios.post('/mentoring/assessment', payload, { withCredentials: true });
      setSubmitted(true);
      setTimeout(() => setShowSuccess(true), 400);
    } catch (err) {
      setError('Fehler beim Speichern. Bitte erneut versuchen.');
    }
  };

  // Nach "Weiter" im Feedback-Popup: Zeige Entscheidungspanel (nur beim Kennenlerntermin)
  const handleShowDecision = () => {
    setShowSuccess(false);
    setShowDecisionPanel(true);
  };

  // Nach Abschluss der Entscheidung / nach "Seite schließen"
  const handleDecisionDone = () => {
    setShowDecisionPanel(false);
    setShowSuccess(false);
    try {
      window.close();
    } catch {
      // Ignorieren, falls das Schließen nicht erlaubt ist
    }
  };

  // Popup für Feedback gespeichert, mit modernem Design und "Weiter"-Button
  const renderSuccessPopup = () => (
    <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50">
      <div className="bg-white rounded-3xl shadow-2xl px-10 py-8 max-w-sm text-center border border-[#53DD6C] relative">
        <svg
          className="w-16 h-16 text-[#53DD6C] mb-4 mx-auto"
          fill="none"
          stroke="currentColor"
          strokeWidth={2}
          viewBox="0 0 24 24"
        >
          <circle cx="12" cy="12" r="10" stroke="#8FFF8B" strokeWidth="2" fill="#E9FFE8" />
          <path strokeLinecap="round" strokeLinejoin="round" d="M7 13l4 4 5-7" stroke="#53DD6C" strokeWidth="3" />
        </svg>
        <h3 className="text-2xl font-bold mb-2 text-gray-900">Feedback gespeichert!</h3>
        <p className="mb-6 text-gray-700">
          Vielen Dank für dein Feedback zum Mentoring-Prozess.
          {milestone === 'kennenlerntermin' && (
            <span className="block mt-2 text-gray-500 text-sm">
              Jetzt kannst du entscheiden, ob du das Mentoring mit deinem Match fortsetzen oder ablehnen möchtest.
            </span>
          )}
        </p>
        {milestone === 'kennenlerntermin' ? (
          <button
            className="mt-2 px-6 py-3 bg-gradient-to-r from-[#8FFF8B] to-[#53DD6C] text-black rounded-xl font-bold shadow hover:from-[#53DD6C] hover:to-[#8FFF8B] transition text-lg"
            onClick={handleShowDecision}
            autoFocus
          >
            Weiter
          </button>
        ) : (
          <button
            className="mt-2 px-6 py-3 bg-gradient-to-r from-[#8FFF8B] to-[#53DD6C] text-black rounded-xl font-bold shadow hover:from-[#53DD6C] hover:to-[#8FFF8B] transition text-lg"
            onClick={handleDecisionDone}
            autoFocus
          >
            Seite schließen
          </button>
        )}
      </div>
    </div>
  );

  // Das DecisionPanel wird nach Feedback gezeigt, aber nur beim Kennenlerntermin
  if (showDecisionPanel && milestone === 'kennenlerntermin') {
    return (
      <MatchDecisionPanel
        menteeId={Number(userId)}
        mentorId={Number(mentorId)}
        role="mentee"
        milestone={milestone}
        onContinue={handleDecisionDone}
        onRejectSuccess={handleDecisionDone}
      />
    );
  }

  return (
    <>
      <form
        className="bg-[#181F29] max-w-xl mx-auto mt-10 rounded-[32px] shadow-2xl p-10 transition-all border border-[#262F41]"
        style={{
          boxShadow: '0 8px 36px 0 rgba(31,38,135,0.18), 0 1.5px 10px 0 rgba(143,255,139,0.08)'
        }}
        onSubmit={handleSubmit}
      >
        <h2 className="text-3xl font-bold text-white mb-9 tracking-tight text-center drop-shadow">
          Feedback zum Mentoring-Prozess
        </h2>

        {error && <p className="text-red-500 text-sm font-semibold mb-4">{error}</p>}

        <div className="mb-8">
          <h3 className="font-semibold mb-3 text-[#8FFF8B]">
            Wie weit hast du deine Ziele erreicht? (1 noch gar nicht - 5 done!)
          </h3>
          {goalsList.length === 0 && <p className="text-gray-400">Keine Ziele verfügbar.</p>}
          {goalsList.map((g) => (
            <div key={g} className="mb-4">
              <label className="block text-base text-[#8FFF8B] mb-2">{g}</label>
              <div className="flex gap-3">
                {SCALE.map((n) => (
                  <button
                    key={n}
                    type="button"
                    onClick={() => handleRate('goal', g, n)}
                    className={`w-10 h-10 rounded-full font-bold
                      ${goals[g] === n
                        ? `bg-gradient-to-br ${MENTIXX_GREEN} text-black border-[#8FFF8B] scale-110 shadow-lg`
                        : 'bg-[#222B39] text-[#8FFF8B] border border-[#282F3F] hover:bg-[#232B34] hover:text-white'}
                    `}
                    aria-label={`Ziel "${g}" Stufe ${n}`}
                  >
                    {n}
                  </button>
                ))}
                <span className="ml-2 text-gray-300">{goals[g] ? `${goals[g]}/5` : ''}</span>
              </div>
            </div>
          ))}
        </div>

        <div className="mb-8">
          <h3 className="font-semibold mb-3 text-[#8FFF8B]">
            Wie schätzt du deine Kompetenzen ein? (1 keine - 5 Experte!)
          </h3>
          {competencesList.length === 0 && <p className="text-gray-400">Keine Kompetenzen verfügbar.</p>}
          {competencesList.map((c) => (
            <div key={c} className="mb-4">
              <label className="block text-base text-[#8FFF8B] mb-2">{c}</label>
              <div className="flex gap-3">
                {SCALE.map((n) => (
                  <button
                    key={n}
                    type="button"
                    onClick={() => handleRate('competence', c, n)}
                    className={`w-10 h-10 rounded-full font-bold
                      ${competences[c] === n
                        ? `bg-gradient-to-br ${MENTIXX_GREEN} text-black border-[#8FFF8B] scale-110 shadow-lg`
                        : 'bg-[#222B39] text-[#8FFF8B] border border-[#282F3F] hover:bg-[#232B34] hover:text-white'}
                    `}
                    aria-label={`Kompetenz "${c}" Stufe ${n}`}
                  >
                    {n}
                  </button>
                ))}
                <span className="ml-2 text-gray-300">{competences[c] ? `${competences[c]}/5` : ''}</span>
              </div>
            </div>
          ))}
        </div>

        <div className="mb-8">
          <label className="block text-base font-semibold text-[#8FFF8B] mb-2">
            Zufriedenheit mit dem Mentoring-Programm (1 gar nicht - 5 SUPER!)
          </label>
          <div className="flex gap-5">
            {SCALE.map((num) => (
              <button
                key={num}
                type="button"
                onClick={() => setSatisfaction(num)}
                className={`
                  w-12 h-12 rounded-full flex items-center justify-center border-2 text-xl font-bold
                  ${satisfaction === num
                    ? `bg-gradient-to-br ${MENTIXX_GREEN} text-black border-[#8FFF8B] scale-110 shadow-lg`
                    : 'bg-[#222B39] text-[#8FFF8B] border-[#282F3F] hover:bg-[#232B34] hover:text-white'}
                `}
                aria-label={`Zufriedenheit Stufe ${num}`}
              >
                {num}
              </button>
            ))}
          </div>
        </div>

        <div className="mb-8">
          <label className="block text-base font-semibold text-[#8FFF8B] mb-2">
            Wie bewertest du deinen Mentor? (1 passt nicht - 5 perfekt Match)
          </label>
          <div className="flex gap-5">
            {SCALE.map((num) => (
              <button
                key={num}
                type="button"
                onClick={() => setMentorRating(num)}
                className={`
                  w-12 h-12 rounded-full flex items-center justify-center border-2 text-xl font-bold
                  ${mentorRating === num
                    ? `bg-gradient-to-br ${MENTIXX_GREEN} text-black border-[#8FFF8B] scale-110 shadow-lg`
                    : 'bg-[#222B39] text-[#8FFF8B] border-[#282F3F] hover:bg-[#232B34] hover:text-white'}
                `}
                aria-label={`Mentor-Bewertung Stufe ${num}`}
              >
                {num}
              </button>
            ))}
          </div>
        </div>

        <div className="mb-10">
          <label className="block text-base font-semibold text-[#8FFF8B] mb-2">
            Freitext (optional)
          </label>
          <textarea
            className="w-full rounded-2xl bg-[#222B39] border border-[#2F3848] p-3 shadow focus:ring-2 focus:ring-[#8FFF8B] focus:bg-[#212B36] text-white placeholder:text-gray-400 transition"
            rows={3}
            placeholder="Was lief besonders gut oder was sollte sich ändern?"
            value={comments}
            onChange={e => setComments(e.target.value)}
            maxLength={500}
          />
        </div>

        <button
          type="submit"
          className={`
            w-full py-3 rounded-2xl font-bold text-lg 
            bg-gradient-to-r from-[#8FFF8B] to-[#45B36C] text-black
            shadow-xl transition-all duration-150 hover:from-[#45B36C] hover:to-[#8FFF8B]
            ${submitted || !isValid ? 'opacity-60 cursor-not-allowed' : ''}
          `}
          disabled={submitted || !isValid}
        >
          Feedback absenden
        </button>

        {submitted && !showSuccess && (
          <div className="mt-8 text-center text-[#8FFF8B] font-bold text-xl">
            Feedback wurde gespeichert. Danke!
          </div>
        )}
      </form>

      {showSuccess && renderSuccessPopup()}
    </>
  );
}

